import { DataGridTypes } from 'devextreme-react/cjs/data-grid'
import { Form as DXForm, GroupItem, SimpleItem, Tab, TabbedItem, TabPanelOptions } from 'devextreme-react/form'
import type { ticket } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { Button, ScrollView } from 'devextreme-react'
import TicketEditorEvents from '@/routes/tickets/TIcketEditor.events'
import NoteView from '@/routes/tickets/notes/NoteView'
import { useTicketNotes } from '@/routes/tickets/notes/ticketNotes'
import { createTicketAttachmentFolder, getTicketAttachmentPermissions } from '@/routes/tickets/ticketWrite'
import { StatoTicket } from '@/routes/tickets/TicketEditor.enums'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { useRef } from 'react'
import AttachmentsManager from '@/components/file-manager/AttachmentsManager'
import { FileManagerRef } from 'devextreme-react/cjs/file-manager'
import TicketEditorDumps from '@/routes/tickets/TicketEditor.dumps'
import { useScreenSize } from '@/themes/media-query'

const TicketBriefDetail = (props: DataGridTypes.MasterDetailTemplateData) => {
  const ticket: ticket = props.data.data
  const fileManagerRef = useRef<FileManagerRef>(null)
  const { client } = useQsAdminApiManager()
  const currentScreenSize = useScreenSize()
  const notesQuery = useTicketNotes({
    id: ticket.id,
    ded_Dis: ticket.ded_Dis,
    attachmentsFolderUrl: ticket.attachmentsFolderUrl,
  })
  const notes = notesQuery.data ?? []

  const renderNotes = () => {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Button
            icon="refresh"
            text="Aggiorna note"
            stylingMode="text"
            disabled={notesQuery.isFetching}
            onClick={() => {
              notesQuery.refetch()
            }}
          />
        </div>
        {notes.length > 0 ? (
          <div className="notes-list">
            {notes.map((nota) => {
              return <NoteView key={nota.id} note={nota} readOnly={true} visibleEditClick={false} />
            })}
          </div>
        ) : notesQuery.isLoading ? (
          <div className="no-notes-message">
            <i className="dx-icon-spin dx-icon-refresh"></i>
            <p>Caricamento note in corso...</p>
          </div>
        ) : notesQuery.isError ? (
          <div className="no-notes-message">
            <i className="dx-icon-warning"></i>
            <p>Errore durante il caricamento delle note</p>
          </div>
        ) : (
          <div className="no-notes-message">
            <i className="dx-icon-info"></i>
            <p>Nessuna nota associata al ticket</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={'dx-card responsive-paddings'}>
      <DXForm id={'ticket_brief_detail_form'} formData={ticket} labelLocation={'top'} readOnly={true}>
        <GroupItem colCount={5} colSpan={5}>
          <GroupItem colCount={1} colSpan={5}>
            <TabbedItem>
              <TabPanelOptions
                height={'atuo'}
                animationEnabled={true}
                swipeEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall}
                deferRendering={true}
                scrollingEnabled={true}
              />
              <Tab title="Note">
                <SimpleItem
                  editorType={'dxHtmlEditor'}
                  label={{ visible: false }}
                  editorOptions={{ readOnly: true }}
                  render={() => {
                    return (
                      <div className="ticket-notes-container" style={{ height: 300 }}>
                        <ScrollView width={'100%'} direction="vertical">
                          {renderNotes()}
                        </ScrollView>
                      </div>
                    )
                  }}
                ></SimpleItem>
              </Tab>
              <Tab title="Motivo">
                <SimpleItem
                  dataField="descrizione"
                  label={{ visible: false }}
                  editorType="dxHtmlEditor"
                  editorOptions={{
                    stylingMode: 'filled',
                    valueType: 'html',
                    readOnly: true,
                    mediaResizing: {
                      enabled: true,
                    },
                    imageUpload: {
                      tabs: ['file', 'url'],
                      fileUploadMode: 'base64',
                    },
                    height: 300,
                  }}
                ></SimpleItem>
              </Tab>
              {ticket?.stato?.id === StatoTicket.COMPLETATO && (
                <Tab title="Soluzione">
                  <SimpleItem
                    dataField="soluzione"
                    label={{ visible: false }}
                    editorType="dxHtmlEditor"
                    editorOptions={{
                      stylingMode: 'filled',
                      valueType: 'html',
                      readOnly: true,
                      mediaResizing: {
                        enabled: true,
                      },
                      imageUpload: {
                        tabs: ['file', 'url'],
                        fileUploadMode: 'base64',
                      },
                      height: 300,
                    }}
                  ></SimpleItem>
                </Tab>
              )}
              {ticket?.stato?.id === StatoTicket.COMPLETATO && ticket.rating && (
                <Tab title="Rating">
                  <SimpleItem
                    dataField="rating_note"
                    label={{ visible: false }}
                    editorType="dxHtmlEditor"
                    editorOptions={{
                      stylingMode: 'filled',
                      valueType: 'html',
                      readOnly: true,
                      mediaResizing: {
                        enabled: true,
                      },
                      imageUpload: {
                        tabs: ['file', 'url'],
                        fileUploadMode: 'base64',
                      },
                      height: 300,
                    }}
                  ></SimpleItem>
                </Tab>
              )}
              <Tab title="Eventi">
                <TicketEditorEvents ticket={ticket} height={300}></TicketEditorEvents>
              </Tab>
              <Tab title="Allegati">
                <AttachmentsManager
                  height={300}
                  fileManagerRef={fileManagerRef}
                  folderUrl={ticket?.attachmentsFolderUrl}
                  preliminarPermissionsAction={async () => {
                    return await getTicketAttachmentPermissions(client, ticket?.id, {
                      objectId: ticket?.id,
                      path: ticket?.attachmentsFolderUrl ?? '',
                    })
                  }}
                  createDirectoryAction={async (rootFolderPath: string, name: string, conflictBehavior: string) => {
                    return await createTicketAttachmentFolder(client, ticket?.id ?? 0, {
                      rootFolder: rootFolderPath,
                      newFolderName: name,
                      conflictBehavior,
                      objectId: ticket?.id,
                    })
                  }}
                />
              </Tab>
              <Tab title="Dump">
                <TicketEditorDumps dumps={ticket.dumps} height={300}></TicketEditorDumps>
              </Tab>
            </TabbedItem>
          </GroupItem>
        </GroupItem>
      </DXForm>
    </div>
  )
}

export default TicketBriefDetail
